import * as React from 'react';
import { cn } from '@wayste/shadcn/lib/utils';
import { type LucideIcon } from 'lucide-react';

// ─── Size context ─────────────────────────────────────────────────────────────
// Lets ToolbarTitle read the parent Toolbar's size without requiring the prop
// to be passed twice.

export type ToolbarSize = 'xs' | 'sm' | 'default' | 'lg';

const ToolbarSizeContext = React.createContext<ToolbarSize>('default');

// ─── Padding & typography maps (from Figma) ───────────────────────────────────

const paddingMap: Record<ToolbarSize, string> = {
    xs: 'px-3 py-2.5',
    sm: 'px-4 py-3',
    default: 'px-5 py-4',
    lg: 'px-6 py-3',
};

const titleTextMap: Record<ToolbarSize, string> = {
    xs: 'text-sm font-semibold leading-5',
    sm: 'text-sm font-semibold leading-5',
    default: 'text-base font-semibold leading-6',
    lg: 'text-lg font-semibold leading-7 tracking-tight',
};

// ─── Toolbar ─────────────────────────────────────────────────────────────────

export interface ToolbarProps extends React.ComponentProps<'div'> {
    /**
     * Controls horizontal padding and vertical padding of the toolbar.
     * - xs  — 12px / 10px  (compact, e.g. nested panels)
     * - sm  — 16px / 12px  (small drawers, modals)
     * - default — 20px / 16px  (standard drawers, modals)
     * - lg  — 24px / 12px  (full-page headers)
     */
    size?: ToolbarSize;
    /** Remove the bottom border (e.g. when toolbar sits above a borderless area). */
    noBorder?: boolean;
}

/**
 * A composable header bar used at the top of modals, drawers, and pages.
 * Place a `ToolbarTitle` inside to get the expanding title area, then add
 * any `Button`, `Pill`, or other elements after it — they'll auto-align to
 * the right edge.
 *
 * @example
 * ```tsx
 * <Toolbar size="default">
 *   <Button variant="ghost" size="icon"><ArrowLeft className="size-4" /></Button>
 *
 *   <ToolbarTitle icon={FileText} description="Fill out the form below.">
 *     Create new deal sheet
 *   </ToolbarTitle>
 *
 *   <Button size="sm" variant="outline">Cancel</Button>
 *   <Button size="sm">Save</Button>
 *   <Button variant="ghost" size="icon"><X className="size-4" /></Button>
 * </Toolbar>
 * ```
 */
export function Toolbar({ size = 'default', noBorder = false, className, children, ...props }: ToolbarProps) {
    return (
        <ToolbarSizeContext.Provider value={size}>
            <div
                data-slot="toolbar"
                className={cn(
                    'bg-surface-tertiary flex w-full items-center gap-3',
                    paddingMap[size],
                    !noBorder && 'border-border-default border-b',
                    className,
                )}
                {...props}
            >
                {children}
            </div>
        </ToolbarSizeContext.Provider>
    );
}

// ─── ToolbarTitle ─────────────────────────────────────────────────────────────

export interface ToolbarTitleProps extends React.ComponentProps<'div'> {
    /** Optional Lucide icon rendered to the left of the title text. */
    icon?: LucideIcon;
    /** Supporting description rendered below the title. */
    description?: string;
}

/**
 * The expanding title area inside a `Toolbar`. Grows to fill available space,
 * pushing any siblings to the right edge.
 *
 * Reads `size` from the parent `Toolbar` context to set the correct title
 * text size automatically.
 */
export function ToolbarTitle({ icon: Icon, description, className, children, ...props }: ToolbarTitleProps) {
    const size = React.useContext(ToolbarSizeContext);

    return (
        <div data-slot="toolbar-title" className={cn('flex min-w-0 flex-1 flex-col justify-center gap-1', className)} {...props}>
            <div className="flex items-center gap-2">
                {Icon && <Icon className="text-text-secondary size-4 shrink-0" aria-hidden="true" />}
                <span className={cn('text-text-primary truncate', titleTextMap[size])}>{children}</span>
            </div>
            {description && <p className="text-text-secondary truncate text-xs leading-4">{description}</p>}
        </div>
    );
}
